import { useFormatter, useTranslations } from 'next-intl';
import { useState } from 'react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/app/components/ui/alert-dialog';
import { Button } from '@/app/components/ui/button';
import { toast } from '@/app/components/ui/use-toast';
import { useFetch, useSubscription } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import type { SubscriptionInfo } from '@/app/stores/slices/user-info';
import { subscriptionsCancelMySubscription } from '../apis';

export const Unsubscribe = ({
  children,
  subscription,
}: {
  children: React.ReactNode;
  subscription: SubscriptionInfo;
}) => {
  const { updateSubscriptionAsync } = useSubscription();
  const { updateRefreshGetProducts } = useStore((state) => ({
    updateRefreshGetProducts: state.updateRefreshGetProducts,
  }));
  const [open, setOpen] = useState(false);
  const format = useFormatter();
  const t = useTranslations();

  const handleCancel = () => {
    setOpen(false);
  };

  const { loading, run: runUnsubscribe } = useFetch(
    (subscription_id: string) =>
      subscriptionsCancelMySubscription({
        body: {
          subscription_id,
        },
      }),
    {
      manual: true,
      onSuccess: ({ error }) => {
        if (error) {
          toast({
            type: 'warning',
            message: t('payments.unsubscribe.error'),
          });
          return;
        }
        setOpen(false);
        toast({
          type: 'success',
          message: t('payments.unsubscribe.success'),
        });
        updateSubscriptionAsync().then(() => {
          updateRefreshGetProducts(true);
        });
      },
    },
  );

  const handleUnsubscribe = () => {
    runUnsubscribe(subscription.id);
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>{children}</AlertDialogTrigger>
      <AlertDialogContent className="w-[640px] rounded-16 border-none bg-system-bg04 text-left opacity-100 sm:w-full">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-left">{t('payments.unsubscribe.title')}</AlertDialogTitle>
        </AlertDialogHeader>
        <AlertDialogDescription className="text-left">
          {t('payments.unsubscribe.description', {
            date: subscription.period_end
              ? format.dateTime(new Date(subscription.period_end), {
                  timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                })
              : '',
          })}
        </AlertDialogDescription>
        <AlertDialogFooter className="flex flex-row justify-end gap-4">
          <Button variant="outline" disabled={loading} className="w-[120px] text-sm leading-[2]" onClick={handleCancel}>
            {t('payments.unsubscribe.cancel')}
          </Button>
          <Button
            variant="primary"
            disabled={loading}
            className="w-[120px] text-sm leading-[2]"
            onClick={handleUnsubscribe}
          >
            {t('payments.unsubscribe.continue')}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
